import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TrendingUp, Clock, Target, AlertTriangle, Lock, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { StrategyMetrics } from '@/hooks/useStrategyAnalysis';

interface Props {
  strategies: {
    scalp: StrategyMetrics;
    swing: StrategyMetrics;
    positional: StrategyMetrics;
  } | null;
  isPro: boolean;
  isLoading?: boolean;
  className?: string;
}

const strategyConfig = {
  scalp: {
    label: 'Scalp',
    description: '< 4 hours',
    icon: Zap,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
  },
  swing: {
    label: 'Swing',
    description: '4h - 7 days',
    icon: TrendingUp,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
  },
  positional: {
    label: 'Positional',
    description: '> 7 days',
    icon: Clock,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
  },
};

export function StrategySegmentationSection({ strategies, isPro, isLoading, className }: Props) {
  const navigate = useNavigate();

  // Generate insights
  const generateInsight = () => {
    if (!strategies) return null;

    const strategyEntries = Object.entries(strategies) as [keyof typeof strategyConfig, StrategyMetrics][];
    const withTrades = strategyEntries.filter(([_, m]) => m.tradeCount >= 3);

    if (withTrades.length < 2) return null;

    const sorted = [...withTrades].sort((a, b) => b[1].sharpe - a[1].sharpe);
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];

    if (best[1].sharpe > worst[1].sharpe && best[0] !== worst[0]) {
      return {
        type: 'success' as const,
        title: 'Strategy Insight',
        message: `Your ${strategyConfig[best[0]].label} trades have significantly better risk-adjusted returns (Sharpe: ${best[1].sharpe.toFixed(2)}) compared to ${strategyConfig[worst[0]].label} trades (Sharpe: ${worst[1].sharpe.toFixed(2)}). Consider allocating more capital to ${strategyConfig[best[0]].label.toLowerCase()} positions.`,
      };
    }

    return null;
  };

  const generateWarning = () => {
    if (!strategies) return null;

    const { scalp, swing, positional } = strategies;
    const totalTrades = scalp.tradeCount + swing.tradeCount + positional.tradeCount;

    if (scalp.tradeCount > swing.tradeCount + positional.tradeCount && scalp.sharpe < 0.5 && totalTrades > 10) {
      return {
        type: 'warning' as const,
        title: 'High Frequency Warning',
        message: `${scalp.tradeCount} scalp trades with Sharpe ratio of ${scalp.sharpe.toFixed(2)} suggests over-trading. Transaction costs and market noise may be eroding your edge. Consider reducing trade frequency and holding positions longer.`,
      };
    }

    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn("space-y-4", className)}>
        <div>
          <h2 className="text-xl font-bold mb-1">Strategy Performance</h2>
          <p className="text-sm text-muted-foreground">Loading strategy analysis...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-32 bg-muted/30 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Pro-gated blur overlay for non-Pro users
  if (!isPro) {
    return (
      <div className={cn("relative", className)}>
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
          <Card className="max-w-md border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Pro Feature Locked
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Unlock strategy segmentation to see which trading style (scalp vs swing vs hold)
                delivers the best risk-adjusted returns. Make data-driven decisions about trade
                frequency and holding periods.
              </p>
              <Button onClick={() => navigate('/pricing')} className="w-full">
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
        <div className="blur-sm pointer-events-none opacity-50">
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Strategy Performance</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['Scalp', 'Swing', 'Positional'].map((s) => (
                <Card key={s}>
                  <CardContent className="pt-6">
                    <div className="h-32 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!strategies) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Strategy Performance
          </CardTitle>
          <CardDescription>No strategy data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const insight = generateInsight();
  const warning = generateWarning();

  const formatCurrency = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    if (Math.abs(value) >= 1000) {
      return `${sign}$${(value / 1000).toFixed(1)}k`;
    }
    return `${sign}$${value.toFixed(2)}`;
  };

  const formatHoldTime = (hours: number) => {
    if (hours < 1) return `${(hours * 60).toFixed(0)}m`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <div>
          <h2 className="text-xl font-bold">Strategy Performance</h2>
          <p className="text-sm text-muted-foreground">
            Compare risk-adjusted returns across trading styles
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(Object.entries(strategies) as [keyof typeof strategyConfig, StrategyMetrics][]).map(([key, metrics]) => {
          const config = strategyConfig[key];
          const Icon = config.icon;

          return (
            <Card key={key} className={cn("relative overflow-hidden", config.borderColor)}>
              <div className={cn("absolute top-0 left-0 w-1 h-full", config.bgColor.replace("/10", ""))} />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={cn("p-1.5 rounded-md", config.bgColor)}>
                      <Icon className={cn("w-4 h-4", config.color)} />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-semibold">{config.label}</CardTitle>
                      <p className="text-xs text-muted-foreground">{config.description}</p>
                    </div>
                  </div>
                  <Badge variant={metrics.totalPnl >= 0 ? "default" : "destructive"} className="font-mono">
                    {formatCurrency(metrics.totalPnl)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Trades</p>
                    <p className="text-lg font-semibold font-mono">{metrics.tradeCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Win Rate</p>
                    <p className={cn(
                      "text-lg font-semibold font-mono",
                      metrics.winRate >= 0.5 ? "text-emerald-500" : "text-rose-500"
                    )}>
                      {(metrics.winRate * 100).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Sharpe</p>
                    <p className={cn(
                      "text-lg font-semibold font-mono",
                      metrics.sharpe >= 0.5 ? "text-emerald-500" : metrics.sharpe >= 0 ? "text-amber-500" : "text-rose-500"
                    )}>
                      {metrics.sharpe.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Hold</p>
                    <p className="text-lg font-semibold font-mono">
                      {formatHoldTime(metrics.avgHoldDurationHours)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg PnL</p>
                    <p className={cn(
                      "text-sm font-mono",
                      metrics.avgPnlPerTrade >= 0 ? "text-emerald-500" : "text-rose-500"
                    )}>
                      {formatCurrency(metrics.avgPnlPerTrade)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Max Loss</p>
                    <p className="text-sm font-mono text-rose-500">
                      {formatCurrency(metrics.maxLoss)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Actionable Insights */}
      {insight && (
        <Alert>
          <TrendingUp className="h-4 w-4" />
          <AlertTitle>{insight.title}</AlertTitle>
          <AlertDescription>{insight.message}</AlertDescription>
        </Alert>
      )}

      {warning && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{warning.title}</AlertTitle>
          <AlertDescription>{warning.message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
